/**
 * CardMarket API Client
 *
 * This client communicates with our Firebase Cloud Function proxy
 * which handles OAuth 1.0a authentication with CardMarket's API.
 *
 * CardMarket API requires:
 * - OAuth 1.0a authentication
 * - Server-side requests (CORS restrictions)
 * - Rate limiting (max 5000 requests/day)
 */

import { db } from '../firebase.js';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const PROXY_BASE_URL = process.env.REACT_APP_CARDMARKET_PROXY_URL || '/api/cardmarket';

export interface CardMarketProduct {
  idProduct: number;
  idMetaproduct: number;
  countReprints: number;
  enName: string;
  locName: string;
  website: string;
  image: string;
  gameName: string;
  categoryName: string;
  number: string;
  rarity: string;
  expansionName: string;
  priceGuide?: {
    SELL: number;
    LOW: number;
    LOWEX: number;
    LOWFOIL: number;
    AVG: number;
    TREND: number;
    TRENDFOIL: number;
  };
}

export interface CardMarketPriceGuide {
  SELL: number;     // Average price of articles sold (30 days)
  LOW: number;      // Lowest price of article in stock
  LOWEX: number;    // Lowest price of article in Excellent or better condition
  LOWFOIL: number;  // Lowest price of foil article in stock
  AVG: number;      // Average price of articles in stock
  TREND: number;    // Market trend price
  TRENDFOIL: number; // Trend price for foil
}

export interface CardMarketArticle {
  idArticle: number;
  idProduct: number;
  language: { idLanguage: number; languageName: string };
  comments: string;
  price: number;
  count: number;
  condition: string;
  isFoil: boolean;
  isSigned: boolean;
  isAltered: boolean;
  isPlayset: boolean;
  seller: {
    idUser: number;
    username: string;
    country: string;
    isCommercial: number;
  };
}

export interface CardMarketSearchResult {
  products: CardMarketProduct[];
  totalResults: number;
}

export interface CardMarketError {
  code: string;
  message: string;
}

class CardMarketClient {
  private cache = new Map<string, { data: unknown; expires: number }>();
  private cacheDuration = 60 * 60 * 1000; // 1 hour

  /**
   * Make a request to the CardMarket proxy
   */
  private async request<T>(
    endpoint: string,
    params?: Record<string, string>
  ): Promise<T> {
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';

    const response = await fetch(`${PROXY_BASE_URL}${endpoint}${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        code: 'UNKNOWN_ERROR',
        message: 'An unknown error occurred',
      }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get from cache or fetch
   */
  private async cachedRequest<T>(
    cacheKey: string,
    fetcher: () => Promise<T>
  ): Promise<T> {
    const cached = this.cache.get(cacheKey);
    if (cached && cached.expires > Date.now()) {
      return cached.data as T;
    }

    const data = await fetcher();
    this.cache.set(cacheKey, {
      data,
      expires: Date.now() + this.cacheDuration,
    });

    return data;
  }

  /**
   * Search for products by name
   */
  async searchProducts(
    query: string,
    options?: {
      game?: string;
      exact?: boolean;
      limit?: number;
    }
  ): Promise<CardMarketSearchResult> {
    const cacheKey = `search:${query}:${JSON.stringify(options)}`;

    return this.cachedRequest(cacheKey, () =>
      this.request<CardMarketSearchResult>('/search', {
        query,
        game: options?.game || 'pokemon',
        exact: options?.exact ? 'true' : 'false',
        limit: String(options?.limit || 10),
      })
    );
  }

  /**
   * Get product details including price guide
   */
  async getProduct(productId: number): Promise<CardMarketProduct | null> {
    const cacheKey = `product:${productId}`;

    try {
      return await this.cachedRequest(cacheKey, () =>
        this.request<CardMarketProduct>(`/products/${productId}`)
      );
    } catch (error) {
      console.error('Failed to get product:', error);
      return null;
    }
  }

  /**
   * Get price guide for a product
   */
  async getPriceGuide(productId: number): Promise<CardMarketPriceGuide | null> {
    const cacheKey = `price:${productId}`;

    try {
      return await this.cachedRequest(cacheKey, () =>
        this.request<CardMarketPriceGuide>(`/products/${productId}/price`)
      );
    } catch (error) {
      console.error('Failed to get price guide:', error);
      return null;
    }
  }

  /**
   * Get articles (listings) for a product
   */
  async getArticles(
    productId: number,
    options?: {
      minCondition?: string;
      language?: number;
      limit?: number;
    }
  ): Promise<CardMarketArticle[]> {
    const cacheKey = `articles:${productId}:${JSON.stringify(options)}`;

    try {
      return await this.cachedRequest(cacheKey, () =>
        this.request<CardMarketArticle[]>(`/products/${productId}/articles`, {
          minCondition: options?.minCondition || 'LP',
          language: String(options?.language || 1), // English
          limit: String(options?.limit || 20),
        })
      );
    } catch (error) {
      console.error('Failed to get articles:', error);
      return [];
    }
  }

  /**
   * Find CardMarket product ID by card name and set
   * Stores mapping in Firestore for faster lookups
   */
  async findProductId(
    cardName: string,
    setName: string,
    cardNumber?: string
  ): Promise<number | null> {
    // Check Firestore cache first
    const mappingKey = `${setName}:${cardNumber || cardName}`.toLowerCase().replace(/\s+/g, '_');

    if (db) {
      try {
        const mappingRef = doc(db, 'cardmarket_mappings', mappingKey);
        const mappingSnap = await getDoc(mappingRef);

        if (mappingSnap.exists()) {
          return mappingSnap.data().productId;
        }
      } catch (error) {
        console.warn('Failed to check mapping cache:', error);
      }
    }

    // Search CardMarket
    const searchQuery = cardNumber
      ? `${cardName} ${setName} ${cardNumber}`
      : `${cardName} ${setName}`;

    try {
      const results = await this.searchProducts(searchQuery, {
        game: 'pokemon',
        limit: 5,
      });

      if (results.products.length === 0) return null;

      // Find best match
      const product = results.products.find(
        (p) =>
          p.enName.toLowerCase().includes(cardName.toLowerCase()) &&
          p.expansionName.toLowerCase().includes(setName.toLowerCase())
      ) || results.products[0];

      // Cache the mapping
      if (db && product) {
        try {
          const mappingRef = doc(db, 'cardmarket_mappings', mappingKey);
          await setDoc(mappingRef, {
            productId: product.idProduct,
            cardName,
            setName,
            cardNumber,
            updatedAt: serverTimestamp(),
          });
        } catch (error) {
          console.warn('Failed to cache mapping:', error);
        }
      }

      return product?.idProduct || null;
    } catch (error) {
      console.error('Failed to find product:', error);
      return null;
    }
  }

  /**
   * Get full price data for a Pokemon TCG card
   */
  async getCardPrices(
    cardName: string,
    setName: string,
    cardNumber?: string
  ): Promise<{
    productId: number;
    priceGuide: CardMarketPriceGuide;
    url: string;
  } | null> {
    const productId = await this.findProductId(cardName, setName, cardNumber);
    if (!productId) return null;

    const priceGuide = await this.getPriceGuide(productId);
    if (!priceGuide) return null;

    return {
      productId,
      priceGuide,
      url: `https://www.cardmarket.com/en/Pokemon/Products/Singles/${encodeURIComponent(setName)}/${encodeURIComponent(cardName)}`,
    };
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Singleton instance
export const cardMarketClient = new CardMarketClient();
